import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, EventEmitter, Component, ChangeDetectionStrategy, Input, Output, NgModule } from '@angular/core';
import { DOCUMENT } from '@angular/common';

const DISQUS_SHORTNAME = new InjectionToken('DISQUS_SHORTNAME');

class DisqusService {
    get DISQUS() {
        return this.document.defaultView['DISQUS'];
    }
    get config() {
        return this.document.defaultView['disqus_config'];
    }
    set config(config) {
        this.document.defaultView['disqus_config'] = config;
    }
    constructor(shortname, document) {
        this.shortname = shortname;
        this.document = document;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.4", ngImport: i0, type: DisqusService, deps: [{ token: DISQUS_SHORTNAME }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.4", ngImport: i0, type: DisqusService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.4", ngImport: i0, type: DisqusService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DISQUS_SHORTNAME]
                }] }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; } });

class Disqus {
    constructor(disqus, renderer, el) {
        this.disqus = disqus;
        this.renderer = renderer;
        this.el = el;
        /** DISQUS events */
        this.newComment = new EventEmitter(true);
        this.ready = new EventEmitter(true);
        this.paginate = new EventEmitter(true);
    }
    ngOnChanges() {
        /** Reset Disqus if any input changed */
        if (!this.disqus.DISQUS) {
            this.addDisqusScript();
        }
        else {
            this.reset();
        }
    }
    /** Add DISQUS script */
    addDisqusScript() {
        /** Set DISQUS config */
        this.disqus.config = this.getConfig();
        const disqusScript = this.renderer.createElement('script');
        disqusScript.src = `//${this.disqus.shortname}.disqus.com/embed.js`;
        disqusScript.async = true;
        disqusScript.type = 'text/javascript';
        this.renderer.setAttribute(disqusScript, 'data-timestamp', new Date().getTime().toString());
        this.renderer.appendChild(this.el.nativeElement, disqusScript);
    }
    /** Reset DISQUS with the new config */
    reset() {
        this.disqus.DISQUS.reset({
            reload: true,
            config: this.getConfig()
        });
    }
    /** Create DISQUS config from the inputs */
    getConfig() {
        const self = this;
        return function () {
            this.page.identifier = self.identifier;
            this.page.url = self.validateUrl(self.url);
            this.page.title = self.title;
            this.category_id = self.category;
            this.language = self.language;
            /* Available callbacks are afterRender, onInit, onNewComment, onPaginate, onReady, preData, preInit, preReset */
            this.callbacks.onNewComment = [(e) => {
                    self.newComment.emit(e);
                }];
            this.callbacks.onReady = [(e) => {
                    self.ready.emit(e);
                }];
            this.callbacks.onPaginate = [(e) => {
                    self.paginate.emit(e);
                }];
        };
    }
    validateUrl(url) {
        /** Validate URL input */
        if (url) {
            const r = /(http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?/;
            if (r.test(url)) {
                return url;
            }
            else {
                console.warn('[Disqus]: Invalid URL');
            }
        }
        /** DISQUS will fallback to "Window.location.href" when URL is undefined */
        return undefined;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.4", ngImport: i0, type: Disqus, deps: [{ token: DisqusService }, { token: i0.Renderer2 }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.4", type: Disqus, isStandalone: true, selector: "disqus", inputs: { url: "url", identifier: "identifier", title: "title", category: "category", language: "language" }, outputs: { newComment: "newComment", ready: "ready", paginate: "paginate" }, usesOnChanges: true, ngImport: i0, template: '<div id="disqus_thread"></div>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.4", ngImport: i0, type: Disqus, decorators: [{
            type: Component,
            args: [{
                    standalone: true,
                    selector: 'disqus',
                    template: '<div id="disqus_thread"></div>',
                    changeDetection: ChangeDetectionStrategy.OnPush
                }]
        }], ctorParameters: function () { return [{ type: DisqusService }, { type: i0.Renderer2 }, { type: i0.ElementRef }]; }, propDecorators: { url: [{
                type: Input
            }], identifier: [{
                type: Input
            }], title: [{
                type: Input
            }], category: [{
                type: Input
            }], language: [{
                type: Input
            }], newComment: [{
                type: Output
            }], ready: [{
                type: Output
            }], paginate: [{
                type: Output
            }] } });

class DisqusModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.4", ngImport: i0, type: DisqusModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.4", ngImport: i0, type: DisqusModule, imports: [Disqus], exports: [Disqus] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.4", ngImport: i0, type: DisqusModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.4", ngImport: i0, type: DisqusModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [Disqus],
                    exports: [Disqus]
                }]
        }] });

/*
 * Public API Surface of ngx-disqus
 */

/**
 * Generated bundle index. Do not edit.
 */

export { DISQUS_SHORTNAME, Disqus, DisqusModule, DisqusService };
